import { defineStore } from 'pinia';

const state = defineStore('quiz', {
  state: () => ({
    pageNum: 0,
    isQuizCompleted: false,
    postcode: {
      value: null,
      required: true,
      valid: true,
    },
    stateCode: '',
    leadEmail: {
      value: '',
      valid: true,
      failed: false,
      success: false,
      sending: false,
    },
    isRegionalQld: false,
    quiz: {},
    localStorageKeys: ['pageNum', 'postcode', 'stateCode', 'quiz', 'isQuizCompleted', 'leadEmail'],
    localStorageKeyPrefix: 'hes_quiz_',
    errorRefs: [],
  }),
  getters: {
    quizRestricted() {
      // exclusions
      const self = this;

      const quiz = {};
      Object.keys(self.quiz).forEach((page) => {
        let validQuestions = 0;
        self.quiz[page].questions.forEach((question) => {
          if (question.apartment_exclude && self.isApartment) {
            return;
          }
          validQuestions += 1;
        });
        if (validQuestions > 0) {
          quiz[page] = self.quiz[page];
        }
      });

      return quiz;
    },
    quizSelections() {
      const mapping = {};
      const self = this;

      Object.keys(this.quiz).forEach((page) => {
        self.quiz[page].questions.forEach((question) => {
          if ((!self.isApartment)
            || (self.isApartment && !question.apartment_exclude)) {
            mapping[question.ref] = [];
            question.selection.forEach((val) => {
              mapping[question.ref].push(val);
            });
          }
        });
      });
      return mapping;
    },
    noQuizPages() {
      return Object.keys(this.quizRestricted).length;
    },
    pageProgressPercentage() {
      return ((this.pageNum + 1) / this.noQuizPages) * 100;
    },
    showGoBackBtn() {
      return this.pageNum !== 0 && this.pageNum !== this.noQuizPages;
    },
    isLastQuizPage() {
      return this.pageNum === this.noQuizPages - 1;
    },
    getPostcode() {
      return this.postcode.value ? this.postcode.value : null;
    },
    getHouseholdSize() {
      let size = 0;
      Object.entries(this.quiz).some((page) => page[1].questions.some((question) => {
        const valid = question.ref === 'householdsize' && question.selection.length === 1;
        if (valid) {
          [size] = question.selection;
          return true;
        }
        return false;
      }));

      return parseInt(size, 10);
    },
    isApartment() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'hometype' && question.selection.length > 0 && question.selection.includes('apartment')));
    },
    isRenter() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'ownershiptype' && question.selection.length > 0 && question.selection.includes('rent')));
    },
    hasGasUtilities() {
      // cooktop
      // hotwater
      // ac
      return Object.entries(this.quiz).some((page) => page[1].questions.some((question) => question.selection.length > 0 && question.selection.includes('gas')));
    },
    getUpgradeableGasItems() {
      const items = [];
      Object.keys(this.quiz).forEach((page) => {
        this.quiz[page].questions.forEach((question) => {
          if (question.selection.includes('gas')) {
            items.push(question.ref);
          }
        });
      });
      return items;
    },
    noUpgradeableHeatingCooling() {
      let num = 0;
      Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => {
            if (question.ref === 'ac') {
              const upgradeableItems = ['ducted', 'underfloor', 'split'];
              num = question.selection
                .reduce((total, val) => total + (upgradeableItems.includes(val) ? 1 : 0), 0);
            }
            return false;
          }));

      return num;
    },
    isCooktopVisible() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'cooktop' && !question.selection.includes('gas')));
    },
    isHotwaterVisible() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'hotwater' && !question.selection.includes('gas')));
    },
    isPartialLEDs() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'lights' && question.selection.includes('some')));
    },
    isSolarSelected() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => !this.isApartment && question.ref === 'solarsystem' && question.selection.length > 0 && !question.selection.includes('0kw')));
    },
    isPetrolVehicle() {
      return Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => question.ref === 'car' && question.selection.includes('ice')));
    },
  },
  actions: {
    leadEmailReset() {
      this.leadEmail.value = '';
      this.leadEmail.valid = true;
      this.leadEmail.failed = false;
      this.leadEmail.sending = false;
      localStorage.setItem(`${this.localStorageKeyPrefix}leadEmail`, JSON.stringify(this.leadEmail));
    },
    resetPostStateCode() {
      this.postcode.value = null;
      this.postcode.valid = true;
      this.stateCode = '';
      this.climateZoneData = [];
      this.isQuizCompleted = false;
      this.pageNum = 0;
    },
    isQuestionValid(page, id) {
      return this.quiz[page].questions[id].valid;
    },
    setStoreToLocal() {
      const self = this;

      this.localStorageKeys.forEach((key) => {
        localStorage.setItem(`${self.localStorageKeyPrefix}${key}`, JSON.stringify(self[`${key}`]));
      });
    },
    setLocalToStore() {
      const self = this;

      this.localStorageKeys.forEach((key) => {
        const ref = localStorage.getItem(`${self.localStorageKeyPrefix}${key}`);
        if (ref) {
          self[key] = JSON.parse(ref);
        }
      });
    },
    getRefValue(ref) {
      let value;
      Object.entries(this.quiz).some((page) => page[1].questions.some((question) => {
        const valid = question.ref === ref && question.selection.length > 0;

        if (valid) {
          value = question.selection;
          return true;
        }
        return false;
      }));

      return value;
    },
    isSavingsActionCompleted(ref) {
      const flag = Object.entries(this.quiz)
        .some((page) => page[1].questions
          .some((question) => {
            let result = false;
            const hasValue = question.selection.length > 0;
            if (question.ref === ref) {
              switch (ref) {
                case 'appliances':
                  result = question.selection.length === 0;
                  break;
                case 'ceiling':
                case 'lights':
                case 'pool':
                  result = hasValue && question.selection[0] === 'yes';
                  break;
                case 'hotwater':
                  result = hasValue && (question.selection[0] === 'heatpump' || question.selection[0] === 'solar');
                  break;
                case 'cooktop':
                  result = hasValue && (question.selection[0] === 'induction' || question.selection[0] === 'ceramic');
                  break;
                case 'ac':
                  result = question.selection.length === 0 || (question.selection.length === 1 && question.selection.includes('split'));
                  break;
                default:
                  break;
              }
            }
            return result;
          }));
      return flag;
    },
    checkQuizQuestions() {
      // cycle through page question fields
      const pageIdx = Object.keys(this.quizRestricted)[this.pageNum];
      const ref = this.quizRestricted[pageIdx];

      ref.questions.forEach((question, q) => {
        if (question.required && question.selection.length === 0) {
          if (this.isApartment && question.apartment_exclude) {
            ref.questions[q].valid = true;
          } else {
            ref.questions[q].valid = false;
            this.errorRefs.push(`${pageIdx}_${question.id}`);
          }
        } else {
          ref.questions[q].valid = true;
        }
      });
    },
    checkQuizQuestion(page, question, q) {
      const ref = this.quizRestricted[page];
      if (ref.questions[q].required && ref.questions[q].selection.length === 0) {
        if (this.isApartment && ref.questions[q].apartment_exclude) {
          ref.questions[q].valid = true;
        } else {
          ref.questions[q].valid = false;
          this.errorRefs.push(`${page}_${question.id}`);
        }
      } else {
        ref.questions[q].valid = true;
      }
    },
  },
});

export default state;
